import styled from "styled-components"

export const Wrapper = styled.div<{ $k?: number }>`
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  opacity: 0;
  ${({ $k = 3, theme }) =>
    theme.animations.types.fade +
    theme.animations.durations.main +
    theme.animations.delays.main($k)}
`

export const HeroIcon = styled.div`
  display: grid;
  place-items: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: rgba(55, 133, 88, 0.12);

  svg {
    width: 44px;
    height: 44px;
    color: ${({ theme }) => theme.colors.green.main};
  }

  @media (max-width: ${({ theme }) => theme.bp.medium}px) {
    width: 72px;
    height: 72px;

    svg {
      width: 40px;
      height: 40px;
    }
  }
`

export const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;

  h1 {
    font-size: 28px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.green.main};
    line-height: 1.2;
    margin: 0;
  }

  p {
    font-size: 16px;
    font-weight: 500;
    color: #374151;
    margin: 0;
  }

  @media (min-width: ${({ theme }) => theme.bp.medium}px) {
    h1 {
      font-size: 32px;
    }
    p {
      font-size: 18px;
    }
  }
`

export const Summary = styled.section`
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 18px;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const SummaryEyebrow = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
`

export const SummaryEventName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  line-height: 1.35;
`

export const SummaryDivider = styled.hr`
  border: 0;
  border-top: 1px solid #e5e7eb;
  margin: 2px 0;
`

export const SummaryItems = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const SummaryItem = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #374151;

  & > span:last-child {
    font-variant-numeric: tabular-nums;
    color: #111827;
    font-weight: 500;
  }
`

export const SummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding-top: 2px;

  & > span:first-child {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
  }

  & > span:last-child {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    font-variant-numeric: tabular-nums;
  }
`

export const Actions = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const actionBase = `
  outline: none;
  cursor: pointer;
  min-height: 48px;
  padding: 0 18px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: box-shadow 0.2s, background-color 0.2s, color 0.2s,
    border-color 0.2s;

  svg {
    width: 20px;
    height: 20px;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`

export const PrimaryAction = styled.button`
  ${actionBase}
  background-color: ${({ theme }) => theme.colors.blue.main};
  color: #fff;
  border: 1px solid ${({ theme }) => theme.colors.blue.main};

  &:hover,
  &:active {
    box-shadow: 0 4px 12px rgba(0, 97, 230, 0.25);
  }
`

export const SecondaryAction = styled.button`
  ${actionBase}
  background-color: ${({ theme }) => theme.colors.blue.main};
  color: ${({ theme }) => theme.colors.white.main};
  transition: background-color 0.3s;
  border: none;

  svg path {
    stroke: ${({ theme }) => theme.colors.white.main};
  }
`

export const WhatsappAction = styled.button`
  ${actionBase}
  background-color: transparent;
  color: ${({ theme }) => theme.colors.blue.main};
  border: 1px solid ${({ theme }) => theme.colors.blue.main};
`

export const TertiaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;
`

export const TertiaryLink = styled.button`
  background: none;
  border: none;
  outline: none;
  padding: 8px 4px;
  min-height: 44px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.blue.main};
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.blue.main};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

export const TertiarySep = styled.span`
  color: #d1d5db;
  font-size: 14px;
  user-select: none;
`

export const EmailNote = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 400;
  text-align: center;

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    color: #9ca3af;
  }

  span {
    word-break: break-word;
  }
`

export const WhatsappSent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  width: 100%;
  padding: 16px 12px;
  border-radius: 12px;
  background-color: rgba(55, 133, 88, 0.08);
`

export const WhatsappSentIcon = styled.div`
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: rgba(55, 133, 88, 0.12);

  svg {
    width: 26px;
    height: 26px;
    color: ${({ theme }) => theme.colors.green.main};
  }
`

export const WhatsappSentMessage = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
`

export const WhatsappSentSubtext = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
`
